// Analytics event taxonomy — web copy of the mobile app's
// `tarbaa-pro-app/src/apis/analytics/events.js`.
//
// Same canonical names on purpose: web and app events land in the SAME
// pipeline (pro-server /api/analytics/log-events + the same Firebase project),
// so a funnel like cta_paywall_shown → paywall_purchase_succeeded aggregates
// across both clients. The `platform: "web"` envelope param separates them.
//
// Convention: domain_object_action, snake_case, ≤40 chars (Firebase limit).
// Every queueEvent() call site MUST use EVENTS.* — never raw strings.
//
// Sync rule: adding an event here means adding it to the app's events.js and
// to ANALYTICS.md in the app repo (renames also need the pro/lite server
// eventAliasMap). Events marked "web only" are not fired by the app.

export const EVENTS = {
  // ── Session / app lifecycle ────────────────────────────────────────────
  SESSION_STARTED: "session_started", // { source: "cold_start" | "foreground" }
  SESSION_ENDED: "session_ended", // { duration_ms }
  SESSION_HEARTBEAT: "session_heartbeat", // periodic flush keep-alive
  SCREEN_VIEWED: "screen_viewed", // { screen_name, previous_screen }
  AUTH_LOGIN_SUCCEEDED: "auth_login_succeeded", // { method }
  AUTH_REGISTER_SUCCEEDED: "auth_register_succeeded", // web only { method }

  // ── Paywall funnel (ordered) ───────────────────────────────────────────
  // cta_paywall_shown → cta_paywall_pressed → paywall_shown → paywall_viewed
  //   → paywall_plan_selected → paywall_purchase_started
  //   → paywall_purchase_{succeeded|failed|cancelled} | paywall_dismissed
  CTA_PAYWALL_SHOWN: "cta_paywall_shown", // { source, reason, feature, tier }
  CTA_PAYWALL_PRESSED: "cta_paywall_pressed", // { source, tier }
  PAYWALL_SHOWN: "paywall_shown", // { source, feature, tier }
  PAYWALL_VIEWED: "paywall_viewed", // { source, packageTypes }
  PAYWALL_PLAN_SELECTED: "paywall_plan_selected", // { plan, source }
  PAYWALL_DISCOUNT_APPLIED: "paywall_discount_applied", // { code, source }
  PAYWALL_PURCHASE_STARTED: "paywall_purchase_started", // { plan, method, source }
  PAYWALL_PURCHASE_SUCCEEDED: "paywall_purchase_succeeded", // { plan, method, source }
  PAYWALL_PURCHASE_FAILED: "paywall_purchase_failed", // { reason, plan, source }
  PAYWALL_PURCHASE_CANCELLED: "paywall_purchase_cancelled", // { plan, source }
  PAYWALL_DISMISSED: "paywall_dismissed", // { time_on_paywall_ms, source }

  // ── Subscription state ─────────────────────────────────────────────────
  SUBSCRIPTION_TIER_CHANGED: "subscription_tier_changed", // { from, to }

  // ── Games ──────────────────────────────────────────────────────────────
  GAME_STARTED: "game_started",
  GAME_CREATE_PRESSED: "game_create_pressed",
  GAME_CREATE_FIRST_PRESSED: "game_create_first_pressed",
  GAME_CAP_REACHED: "game_cap_reached", // { count, limit }
  GAME_WEB_CREATED: "game_web_created",
  GAME_DELETED: "game_deleted",

  // ── Modes (custom / community) ─────────────────────────────────────────
  MODE_CREATED: "mode_created",
  MODE_CREATE_SCREEN_VIEWED: "mode_create_screen_viewed",
  MODE_CREATE_LIMIT_REACHED: "mode_create_limit_reached", // { count }
  MODE_ADDED_BY_CODE: "mode_added_by_code",
  COMMUNITY_MODE_OPENED: "community_mode_opened", // { modeId }

  // ── Live spectator (viewer side + web game-master broadcast) ───────────
  LIVE_VIEWER_JOINED: "live_viewer_joined", // { sessionCode, team }
  LIVE_VOTE_CAST: "live_vote_cast", // { group }
  LIVE_SESSION_STARTED: "live_session_started", // { gameId } — web GM went live
  LIVE_SESSION_ENDED: "live_session_ended", // { reason }
  LIVE_CONSENT_DECLINED: "live_consent_declined",
};

// Screen names for screen_viewed — route path → app-style screen name, so web
// and app screen funnels are comparable. Anything unmatched falls back to the
// pathname with ids stripped.
export const ROUTE_SCREEN_NAMES = {
  "/": "web_home",
  "/links": "links",
  "/scan": "qr_scan",
  "/login": "login",
  "/register": "register",
  "/password-reset": "password_reset",
  "/password-reset-request": "password_reset_request",
  "/mode-creation-terms": "mode_creation_terms",
  "/account-deletion": "account_deletion",
  "/account-deletion-request": "account_deletion_request",
  "/account-deleted": "account_deleted",
  "/pricing": "pricing",
  "/checkout/callback": "checkout_callback",
  "/app": "games_list",
  "/app/games": "games_list",
  "/app/games/create": "game_create",
  "/app/community": "community_modes",
  "/app/community/search": "community_search",
  "/app/modes/create": "mode_create",
  "/app/profile": "profile",
};
